import {Button, Intent} from '@blueprintjs/core';
import {IconNames} from '@blueprintjs/icons';
import {Confirm} from '@zaiusinc/hera';
import {observer} from 'mobx-react';
import {useLocalStore} from 'mobx-react-lite';
import React from 'react';
import {useHistory} from 'react-router-dom';
import {addToQuery} from '../../lib/history';
import {showToasts} from '../../lib/toaster';
import {AppDetailStore} from '../../stores/AppDetailStore';
import { directoryStore } from '../../stores/DirectoryStore';
import {SegmentAnalytics} from '../../lib/SegmentAnalytics';
interface Props {
  detailStore: AppDetailStore;
  history: ReturnType<typeof useHistory>;
}

export const UninstallButton = observer(function UninstallButton({detailStore, history}: Props) {
  const state = useLocalStore(() => ({
    showUninstallModal: false,
    showCannotUninstallModal: false,
    confirmationMessage: '',
    loading: false,
  }));

  const getDefaultConfirmationMessage = (): string => {
    const detail = detailStore.detail;
    const appName = detail ? detail.display_name : 'this app';
    return `Are you sure you want to uninstall the ${appName} app?`;
  };

  const onUninstallClick = async () => {
    state.loading = true;

    try {
      const {uninstallable, message} = await detailStore.canUninstall();

      if (uninstallable) {
        state.showUninstallModal = true;
        state.confirmationMessage = message || getDefaultConfirmationMessage();
      } else {
        state.showCannotUninstallModal = true;
        state.confirmationMessage = message || 'Sorry, this app cannot be uninstalled at this time.';
      }
    } catch (e) {
      console.error(e);
      const message = 'There was an error while checking if the app can be uninstalled.';
      showToasts([{intent: Intent.DANGER, message}]);
    } finally {
      state.loading = false;
    }
  };

  const onCloseModal = () => {
    state.showUninstallModal = false;
    state.showCannotUninstallModal = false;
  };

  const onConfirmUninstall = async () => {
    const {pathname, search} = window.location;
    const wasInstalled = detailStore.installed;
    const success = await detailStore.uninstall();
    SegmentAnalytics.track('App Action', {
      page: new URLSearchParams(search).get('tab') || 'overview',
      path: pathname + search,
      action: 'Uninstall',
      success,
      appId: detailStore.appId,
      appVersion: detailStore.version,
      isInstalled: wasInstalled,
    });
    addToQuery(history, {tab: 'overview'});
    state.showUninstallModal = false;

    const {detail} = detailStore;
    const appName = detail ? detail.display_name : 'This app';
    if (success) {
      showToasts([{intent: 'success', message: `${appName} was uninstalled successfully`}]);
    }
  };

  const {installed, sameVersionInstalled, uninstalling, detail} = detailStore;

  if (!installed || !sameVersionInstalled) {
    return null;
  }

  const appName = detail ? detail.display_name : 'this app';

  return (
    <div>
      <Button
        minimal
        intent={Intent.NONE}
        disabled={uninstalling}
        onClick={onUninstallClick}
        loading={uninstalling || state.loading}
      >
        Uninstall App
      </Button>
      <Confirm
        intent="danger"
        icon={IconNames.WARNING_SIGN}
        confirmText="Uninstall"
        isOpen={state.showUninstallModal}
        onClose={onCloseModal}
        onConfirm={onConfirmUninstall}
        title={`Uninstall ${appName}?`}
      >
        <p>{state.confirmationMessage}</p>
        <ul>
          <li>Uninstalling the app will disable all features of the app.</li>
          <li>{ directoryStore.getAccountType() } data generated by the app will NOT be deleted.</li>
          {detailStore.hasOpal && (
          <li>If Opal tools are added to Opal, the tools will stop working properly in Opal after uninstalling.</li>
          )}
          {detailStore.hasCMSUIExtensions && (
          <li>If CMS UI extensions are enabled, they will stop working in CMS after uninstalling.</li>
          )}
        </ul>
      </Confirm>
      <Confirm
        intent="danger"
        icon={IconNames.WARNING_SIGN}
        cancelDisabled={true}
        confirmText="OK"
        isOpen={state.showCannotUninstallModal}
        onClose={onCloseModal}
        onConfirm={onCloseModal}
        title={`Unable to uninstall ${appName}`}
      >
        {state.confirmationMessage}
      </Confirm>
    </div>
  );
});
